import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Paper,
    Avatar,
    Button,
    Divider,
    Alert,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    TextField,
    Tab,
    Tabs,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Warning,
    CheckCircle,
    Cancel,
    CleaningServices,
    ArrowBack,
    Dangerous,
    BatteryChargingFull,
    LocalPharmacy,
    LocationOn,
    Phone,
    Schedule,
    Timer,
    Security,
    Science,
    Home,
    DirectionsCar,
    ReportProblem,
    HealthAndSafety,
    CallEnd,
    AttachMoney
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const HazardousWastePage = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [tabValue, setTabValue] = useState(0);
    const [monthlyHazardous, setMonthlyHazardous] = useState(5);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const hazardousItems = [
        {
            category: "Batteries & Electronics",
            icon: <BatteryChargingFull sx={{ fontSize: 40, color: '#ff9800' }} />,
            items: ["Car batteries", "Cell phone batteries", "Laptop batteries", "Watch batteries", "Power tools", "LED bulbs", "Circuit boards", "Chargers"],
            color: "#ff9800",
            danger: "high",
            hazardType: "Chemical/Electrical",
            disposalMethod: "Specialized collection",
            healthRisk: "Acid burns, heavy metal poisoning"
        },
        {
            category: "Chemicals & Cleaning",
            icon: <CleaningServices sx={{ fontSize: 40, color: '#f44336' }} />,
            items: ["Paint", "Pesticides", "Bleach", "Drain cleaners", "Motor oil", "Solvents", "Acids", "Rust removers"],
            color: "#f44336",
            danger: "extreme",
            hazardType: "Chemical/Toxic",
            disposalMethod: "Hazmat facility",
            healthRisk: "Poisoning, chemical burns, respiratory issues"
        },
        {
            category: "Medical Waste",
            icon: <LocalPharmacy sx={{ fontSize: 40, color: '#9c27b0' }} />,
            items: ["Expired medicines", "Syringes", "Thermometers", "Blood pressure monitors", "Medical devices", "X-ray films", "Chemotherapy drugs"],
            color: "#9c27b0",
            danger: "high",
            hazardType: "Biological/Chemical",
            disposalMethod: "Medical facility return",
            healthRisk: "Infection, contamination, drug interactions"
        },
        {
            category: "Automotive & Industrial",
            icon: <DirectionsCar sx={{ fontSize: 40, color: '#607d8b' }} />,
            items: ["Antifreeze", "Brake fluid", "Transmission fluid", "Used oil filters", "Gasoline", "Diesel", "Hydraulic oil", "Coolants"],
            color: "#607d8b",
            danger: "extreme",
            hazardType: "Flammable/Toxic",
            disposalMethod: "Auto service centers",
            healthRisk: "Poisoning, fire hazard, environmental contamination"
        },
        {
            category: "Household Hazardous",
            icon: <Home sx={{ fontSize: 40, color: '#795548' }} />,
            items: ["Aerosol cans", "Propane tanks", "Fire extinguishers", "Pool chemicals", "Oven cleaners", "Nail polish", "Wood stains", "Glues"],
            color: "#795548",
            danger: "high",
            hazardType: "Pressurized/Chemical",
            disposalMethod: "HHW collection sites",
            healthRisk: "Explosion, inhalation, skin irritation"
        }
    ];

    const emergencyProtocols = [
        {
            scenario: "Chemical Spill",
            immediateActions: [
                "Evacuate the area immediately",
                "Avoid breathing vapors",
                "Call emergency services: 999",
                "Do not attempt cleanup without protection"
            ],
            preventionTips: [
                "Store chemicals in original containers",
                "Keep away from heat sources",
                "Ensure proper ventilation"
            ],
            icon: "🧪",
            urgency: "CRITICAL"
        },
        {
            scenario: "Battery Leak",
            immediateActions: [
                "Wear gloves and eye protection",
                "Ventilate the area",
                "Clean with baking soda solution",
                "Dispose of contaminated materials safely"
            ],
            preventionTips: [
                "Remove old batteries promptly",
                "Store in cool, dry places",
                "Check expiration dates regularly"
            ],
            icon: "🔋",
            urgency: "HIGH"
        },
        {
            scenario: "Medicine Overdose Risk",
            immediateActions: [
                "Secure all medications immediately",
                "Call Poison Control: 16263",
                "Keep original packaging for reference",
                "Do not induce vomiting unless instructed"
            ],
            preventionTips: [
                "Use medicine take-back programs",
                "Lock medication storage",
                "Dispose of expired drugs properly"
            ],
            icon: "💊",
            urgency: "CRITICAL"
        },
        {
            scenario: "Fuel/Oil Spill",
            immediateActions: [
                "Eliminate ignition sources",
                "Contain spill with absorbent materials",
                "Call fire department: 199",
                "Avoid direct skin contact"
            ],
            preventionTips: [
                "Use proper containers for storage",
                "Check for leaks regularly",
                "Keep fire extinguisher nearby"
            ],
            icon: "⛽",
            urgency: "CRITICAL"
        }
    ];

    const bangladeshHazardousStats = {
        generated: "50K tons/year",
        properDisposal: "5%",
        healthIncidents: "2K+ annually",
        facilities: "12 nationwide",
        compliance: "15%",
        costPerTon: "৳25,000"
    };

    const disposalLocations = [
        {
            city: "Dhaka",
            facilities: ["DNCC Hazmat Center", "Square Pharmaceuticals Disposal", "Industrial Waste Management Ltd"],
            contact: "+880-2-8391215",
            emergencyHotline: "+880-2-9999999",
            services: ["Chemical", "Medical", "Electronic", "Automotive"],
            schedule: "Mon-Fri 9 AM - 4 PM",
            specialInstructions: "Appointment required for large quantities"
        },
        {
            city: "Chittagong",
            facilities: ["CCC Environmental Facility", "Port Hazmat Disposal", "Chittagong Medical Waste Center"],
            contact: "+880-31-2853333",
            emergencyHotline: "+880-31-999999",
            services: ["Industrial", "Medical", "Chemical"],
            schedule: "Mon-Sat 8 AM - 3 PM",
            specialInstructions: "Industrial permit may be required"
        },
        {
            city: "Sylhet",
            facilities: ["Sylhet Medical Disposal", "Tea Industry Waste Center"],
            contact: "+880-821-714491",
            emergencyHotline: "+880-821-999999",
            services: ["Medical", "Agricultural", "Chemical"],
            schedule: "Mon-Fri 10 AM - 3 PM",
            specialInstructions: "Small quantities only"
        }
    ];

    const safetySteps = [
        {
            label: "Identification & Assessment",
            description: "Identify the type of hazardous material and assess the risks involved using safety data sheets"
        },
        {
            label: "Safety Preparation",
            description: "Gather appropriate personal protective equipment (PPE) including gloves, masks, and eye protection"
        },
        {
            label: "Secure Containment",
            description: "Keep materials in original containers or transfer to approved hazmat containers with proper labeling"
        },
        {
            label: "Contact Professionals",
            description: "Call authorized hazardous waste disposal services or take to designated collection points"
        },
        {
            label: "Documentation",
            description: "Keep records of disposal for regulatory compliance and environmental responsibility"
        },
        {
            label: "Area Decontamination",
            description: "Clean the storage area thoroughly and dispose of any contaminated cleaning materials properly"
        }
    ];

    const safetyTips = [
        {
            title: "Personal Protection",
            description: "Always wear appropriate PPE when handling hazardous materials",
            icon: "🛡️",
            importance: "CRITICAL",
            timeRequired: "2-5 minutes",
            cost: "৳500-2000"
        },
        {
            title: "Proper Storage",
            description: "Store chemicals in original containers in cool, dry, ventilated areas",
            icon: "📦",
            importance: "HIGH",
            timeRequired: "10-15 minutes",
            cost: "৳200-1000"
        },
        {
            title: "Emergency Planning",
            description: "Have emergency contact numbers and spill kits readily available",
            icon: "📞",
            importance: "CRITICAL",
            timeRequired: "30 minutes setup",
            cost: "৳1000-3000"
        },
        {
            title: "Regular Inspections",
            description: "Check containers monthly for leaks, corrosion, or deterioration",
            icon: "🔍",
            importance: "HIGH",
            timeRequired: "15-30 minutes",
            cost: "No cost"
        },
        {
            title: "Proper Labeling",
            description: "Keep all original labels and add date of storage",
            icon: "🏷️",
            importance: "MEDIUM",
            timeRequired: "5 minutes",
            cost: "৳50-200"
        },
        {
            title: "Training & Education",
            description: "Ensure all household members know hazardous material locations and risks",
            icon: "📚",
            importance: "HIGH",
            timeRequired: "1-2 hours",
            cost: "No cost"
        }
    ];

    const regulatoryInfo = [
        {
            regulation: "Environment Conservation Act 1995",
            description: "Primary environmental protection law governing hazardous waste",
            penalties: "Fine up to ৳10 lakh or 2 years imprisonment",
            compliance: "Mandatory for businesses"
        },
        {
            regulation: "Hazardous Waste Management Rules 2008",
            description: "Specific rules for collection, treatment, and disposal",
            penalties: "Fine up to ৳5 lakh",
            compliance: "All hazardous waste generators"
        },
        {
            regulation: "Medical Waste Management Rules 2008",
            description: "Guidelines for healthcare-related hazardous materials",
            penalties: "License cancellation possible",
            compliance: "Healthcare facilities"
        }
    ];

    const dosDonts = {
        dos: [
            "Use designated hazardous waste collection centers",
            "Check community collection event schedules",
            "Keep items in original containers when possible",
            "Read and follow all safety instructions",
            "Contact local authorities for guidance",
            "Wear protective equipment during handling",
            "Store away from children and pets",
            "Report spills or accidents immediately"
        ],
        donts: [
            "Put hazardous waste in regular trash",
            "Pour chemicals down drains or toilets",
            "Burn or incinerate hazardous materials",
            "Mix different types of hazardous waste",
            "Leave hazardous waste accessible to children",
            "Store near heat sources or flames",
            "Ignore safety data sheet instructions",
            "Attempt DIY disposal of unknown substances"
        ]
    };

    const calculateHazardousImpact = () => {
        const disposalCost = 25000; // BDT per ton
        const annualCost = (monthlyHazardous * 12 / 1000) * disposalCost;
        const healthBenefit = annualCost * 5; // Health benefit multiplier
        const environmentalProtection = annualCost * 3;
        return { annualCost, healthBenefit, environmentalProtection };
    };

    const { annualCost, healthBenefit, environmentalProtection } = calculateHazardousImpact();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #d84315 0%, #ff5722 100%)',
                    color: 'white',
                    py: { xs: 4, sm: 6, md: 8 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        zIndex: 1
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Button
                                component={Link}
                                to="/"
                                startIcon={<ArrowBack />}
                                sx={{
                                    color: 'white',
                                    mb: { xs: 2, sm: 3 },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                            >
                                Back to Home
                            </Button>

                            <Alert
                                severity="error"
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                    '& .MuiAlert-icon': { color: '#f44336' },
                                    '& .MuiAlert-message': {
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }
                                }}
                            >
                                <Typography fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    ⚠️ DANGER: Improper disposal of hazardous waste can cause serious health and environmental damage!
                                </Typography>
                            </Alert>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: { xs: 2, sm: 3 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        width: { xs: 60, sm: 70, md: 80 },
                                        height: { xs: 60, sm: 70, md: 80 },
                                        mr: { xs: 0, sm: 2, md: 3 },
                                        mb: { xs: 2, sm: 0 }
                                    }}
                                >
                                    <Warning sx={{ fontSize: { xs: 35, sm: 45, md: 50 }, color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="h2"
                                        fontWeight={800}
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' }
                                        }}
                                    >
                                        Hazardous Waste
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
                                        }}
                                    >
                                        Protect people and environment through proper disposal
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                                    lineHeight: 1.6,
                                    opacity: 0.95
                                }}
                            >
                                Hazardous waste contains toxic, corrosive, flammable, or reactive materials that can harm
                                human health and the environment. These materials require special handling and disposal methods.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: { xs: 150, sm: 200, md: 300 },
                                    position: 'relative'
                                }}
                            >
                                <Dangerous sx={{
                                    fontSize: { xs: 100, sm: 150, md: 200 },
                                    color: 'rgba(255,255,255,0.3)'
                                }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
                {/* Bangladesh Hazardous Waste Statistics */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                        border: '2px solid #f44336'
                    }}
                >
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        textAlign="center"
                        gutterBottom
                        sx={{
                            mb: { xs: 2, sm: 3, md: 4 },
                            color: '#c62828',
                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                        }}
                    >
                        🇧🇩 Bangladesh Hazardous Waste Crisis
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {Object.entries(bangladeshHazardousStats).map(([key, value], index) => (
                            <Grid item xs={6} sm={4} md={2} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#f44336"
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Hazardous Waste Impact Calculator */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
                        border: '2px solid #673ab7'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        textAlign: { xs: 'center', sm: 'left' }
                    }}>
                        <Security sx={{
                            color: '#673ab7',
                            fontSize: { xs: 30, sm: 35, md: 40 },
                            mr: { xs: 0, sm: 2 },
                            mb: { xs: 1, sm: 0 }
                        }} />
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            color="#4527a0"
                            sx={{
                                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                            }}
                        >
                            🛡️ Safety Impact Calculator
                        </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Monthly Hazardous Waste (kg)"
                                type="number"
                                value={monthlyHazardous}
                                onChange={(e) => setMonthlyHazardous(Number(e.target.value))}
                                fullWidth
                                sx={{ mb: 2 }}
                                helperText="Include batteries, chemicals, medicines"
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ textAlign: 'center' }}>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#f44336"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        ৳{Math.round(annualCost)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Proper Disposal Cost
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#4caf50"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        ৳{Math.round(healthBenefit)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Health Benefit Value
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#2196f3"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        ৳{Math.round(environmentalProtection)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Environmental Protection
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Enhanced Hazardous Categories with Tabs */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#d84315',
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                    }}
                >
                    Types of Hazardous Materials
                </Typography>

                <Paper sx={{ mb: { xs: 4, sm: 6, md: 8 }, borderRadius: 4 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        centered={!isMobile}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                        sx={{
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 }
                            }
                        }}
                    >
                        <Tab label="Material Categories" />
                        <Tab label="Emergency Protocols" />
                        <Tab label="Safety Guidelines" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={4} sx={{ p: 3 }}>
                            {hazardousItems.map((category, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Card
                                        elevation={4}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 4,
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${category.danger === 'extreme' ? '#f44336' : '#ff9800'}`,
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: '0 12px 40px rgba(244, 67, 54, 0.2)'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 4 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: `${category.color}20`,
                                                            width: 60,
                                                            height: 60,
                                                            mr: 2
                                                        }}
                                                    >
                                                        {category.icon}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="h5" fontWeight={600} color={category.color}>
                                                            {category.category}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {category.hazardType}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    label={category.danger === 'extreme' ? 'EXTREME DANGER' : 'HIGH RISK'}
                                                    color={category.danger === 'extreme' ? 'error' : 'warning'}
                                                    size="small"
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    <strong>Disposal Method:</strong> {category.disposalMethod}
                                                </Typography>
                                                <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
                                                    <strong>Health Risk:</strong> {category.healthRisk}
                                                </Typography>
                                            </Box>

                                            <Grid container spacing={1}>
                                                {category.items.map((item, itemIndex) => (
                                                    <Grid item xs={12} sm={6} key={itemIndex}>
                                                        <Chip
                                                            label={item}
                                                            variant="outlined"
                                                            size="small"
                                                            icon={<Warning />}
                                                            sx={{
                                                                borderColor: category.color,
                                                                color: category.color,
                                                                width: '100%',
                                                                justifyContent: 'flex-start',
                                                                mb: 1,
                                                                '& .MuiChip-icon': {
                                                                    color: category.color
                                                                }
                                                            }}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Grid container spacing={4} sx={{ p: 3 }}>
                            {emergencyProtocols.map((protocol, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 4,
                                            border: `3px solid ${protocol.urgency === 'CRITICAL' ? '#f44336' : '#ff9800'}`
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="h2" sx={{ mr: 2 }}>
                                                    {protocol.icon}
                                                </Typography>
                                                <Box>
                                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                                        {protocol.scenario}
                                                    </Typography>
                                                    <Chip
                                                        label={protocol.urgency}
                                                        color={protocol.urgency === 'CRITICAL' ? 'error' : 'warning'}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>

                                            <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                                                IMMEDIATE ACTIONS:
                                            </Typography>
                                            <List dense>
                                                {protocol.immediateActions.map((action, idx) => (
                                                    <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 20 }}>
                                                            <ReportProblem sx={{ fontSize: 16, color: '#f44336' }} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={action} />
                                                    </ListItem>
                                                ))}
                                            </List>

                                            <Typography variant="subtitle2" color="info.main" sx={{ mb: 1, mt: 2 }}>
                                                PREVENTION:
                                            </Typography>
                                            <List dense>
                                                {protocol.preventionTips.map((tip, idx) => (
                                                    <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 20 }}>
                                                            <HealthAndSafety sx={{ fontSize: 16, color: '#2196f3' }} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={tip} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={4} sx={{ p: 3 }}>
                            {safetyTips.map((tip, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            p: 3,
                                            borderRadius: 4,
                                            height: '100%',
                                            transition: 'all 0.3s ease',
                                            border: '2px solid transparent',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                borderColor: tip.importance === 'CRITICAL' ? '#f44336' : '#ff9800',
                                                boxShadow: '0 8px 25px rgba(244, 67, 54, 0.15)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                                            <Typography variant="h2" sx={{ mb: 2 }}>
                                                {tip.icon}
                                            </Typography>
                                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                                {tip.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {tip.description}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            <Chip
                                                label={tip.importance}
                                                color={tip.importance === 'CRITICAL' ? 'error' : tip.importance === 'HIGH' ? 'warning' : 'info'}
                                                size="small"
                                            />
                                            <Chip
                                                label={tip.timeRequired}
                                                color="info"
                                                size="small"
                                                icon={<Timer />}
                                            />
                                            <Chip
                                                label={tip.cost}
                                                color="secondary"
                                                size="small"
                                                icon={<AttachMoney />}
                                            />
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>
                </Paper>

                {/* Safe Disposal Process */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#d84315' }}>
                    Safe Disposal Process
                </Typography>

                <Paper sx={{ p: 4, mb: 8, borderRadius: 4 }}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {safetySteps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    optional={
                                        index === safetySteps.length - 1 ? (
                                            <Typography variant="caption">Final step</Typography>
                                        ) : null
                                    }
                                >
                                    <Typography variant="h6" fontWeight={600}>
                                        {step.label}
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography sx={{ mb: 2 }}>
                                        {step.description}
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => setActiveStep(index + 1)}
                                            sx={{ mt: 1, mr: 1 }}
                                            disabled={index === safetySteps.length - 1}
                                            color={index < 2 ? "error" : "primary"}
                                        >
                                            {index === safetySteps.length - 1 ? 'Complete' : 'Continue'}
                                        </Button>
                                        <Button
                                            disabled={index === 0}
                                            onClick={() => setActiveStep(index - 1)}
                                            sx={{ mt: 1, mr: 1 }}
                                        >
                                            Back
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                    {activeStep === safetySteps.length && (
                        <Paper square elevation={0} sx={{ p: 3, bgcolor: '#e8f5e8' }}>
                            <Typography>✅ Safety protocol completed! You're ready for proper hazardous waste disposal.</Typography>
                            <Button onClick={() => setActiveStep(0)} sx={{ mt: 1, mr: 1 }}>
                                Reset Guide
                            </Button>
                        </Paper>
                    )}
                </Paper>

                {/* Local Disposal Facilities */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#d84315' }}>
                    🏥 Authorized Disposal Facilities
                </Typography>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    {disposalLocations.map((location, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card
                                elevation={3}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    height: '100%',
                                    border: '2px solid #f44336'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <LocationOn sx={{ color: '#f44336', mr: 1 }} />
                                    <Typography variant="h5" fontWeight={600} color="#c62828">
                                        {location.city}
                                    </Typography>
                                </Box>

                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Authorized Facilities:
                                </Typography>
                                <List dense>
                                    {location.facilities.map((facility, idx) => (
                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 20 }}>
                                                <Security sx={{ fontSize: 16, color: '#f44336' }} />
                                            </ListItemIcon>
                                            <ListItemText primary={facility} />
                                        </ListItem>
                                    ))}
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Accepted Materials:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {location.services.map((service, idx) => (
                                            <Chip
                                                key={idx}
                                                label={service}
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Phone sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2">{location.contact}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <CallEnd sx={{ color: '#f44336', mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2" color="error.main">
                                        Emergency: {location.emergencyHotline}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Schedule sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2">{location.schedule}</Typography>
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                    ⚠️ {location.specialInstructions}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Regulatory Information */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#d84315' }}>
                    📋 Legal Requirements
                </Typography>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    {regulatoryInfo.map((reg, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card elevation={3} sx={{ p: 3, borderRadius: 4, height: '100%' }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom color="#1976d2">
                                    {reg.regulation}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {reg.description}
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <Chip label={reg.compliance} color="info" size="small" />
                                </Box>
                                <Typography variant="body2" color="error.main">
                                    <strong>Penalties:</strong> {reg.penalties}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Enhanced Do's and Don'ts */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#d84315' }}>
                    Critical Safety Guidelines
                </Typography>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)',
                                border: '2px solid #4caf50'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CheckCircle sx={{ color: '#4caf50', fontSize: 40, mr: 2 }} />
                                <Typography variant="h4" fontWeight={700} color="#2e7d32">
                                    ESSENTIAL DO's
                                </Typography>
                            </Box>
                            <List>
                                {dosDonts.dos.map((item, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <CheckCircle sx={{ color: '#4caf50' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                                border: '2px solid #f44336'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Cancel sx={{ color: '#f44336', fontSize: 40, mr: 2 }} />
                                <Typography variant="h4" fontWeight={700} color="#c62828">
                                    NEVER DO
                                </Typography>
                            </Box>
                            <List>
                                {dosDonts.donts.map((item, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <Cancel sx={{ color: '#f44336' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Enhanced Call to Action */}
                <Paper
                    elevation={6}
                    sx={{
                        p: 6,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffffff 0%, #fff8e1 100%)',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h3" fontWeight={700} gutterBottom color="#d84315">
                        ⚠️ Your Safety & Environmental Responsibility
                    </Typography>
                    <Divider sx={{ my: 3, maxWidth: 200, mx: 'auto', backgroundColor: '#ff5722' }} />

                    <Grid container spacing={4} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                                <HealthAndSafety sx={{ fontSize: 48, color: '#f44336', mb: 2 }} />
                                <Typography variant="h5" fontWeight={600} gutterBottom>
                                    Protect Health
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Proper disposal saves ৳{Math.round(healthBenefit)} in health costs annually
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                                <Security sx={{ fontSize: 48, color: '#2196f3', mb: 2 }} />
                                <Typography variant="h5" fontWeight={600} gutterBottom>
                                    Environmental Safety
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Protecting ৳{Math.round(environmentalProtection)} worth of environmental resources
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                                <Science sx={{ fontSize: 48, color: '#9c27b0', mb: 2 }} />
                                <Typography variant="h5" fontWeight={600} gutterBottom>
                                    Legal Compliance
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Avoid fines up to ৳10 lakh and legal consequences
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Alert severity="error" sx={{ mb: 4 }}>
                        <Typography fontWeight={600}>
                            Remember: There is ZERO tolerance for improper hazardous waste disposal. Always prioritize safety!
                        </Typography>
                    </Alert>

                    <Box sx={{ mt: 6 }}>
                        <Button
                            component={Link}
                            to="/classify"
                            variant="contained"
                            size="large"
                            color="error"
                            sx={{
                                mr: 2,
                                px: 4,
                                py: 2,
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)'
                                }
                            }}
                        >
                            Check Material Safety
                        </Button>
                        <Button
                            component={Link}
                            to="/tips"
                            variant="outlined"
                            size="large"
                            color="error"
                            sx={{ px: 4, py: 2 }}
                        >
                            Safety Guidelines
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default HazardousWastePage;
